import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Switch, 
  Button, 
  message,
  Row,
  Col,
  Upload,
  Image,
  Space,
  Tag,
  Tree,
  Tabs
} from 'antd';
import { 
  SaveOutlined, 
  ArrowLeftOutlined,
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { productService } from '../services/productService';
import { commonAttachFileService } from '../services/commonAttachFileService';
import { specService } from '../services/specService';
import type { Product, SpecDetail } from '../types/product';
import { CATEGORY_MAP, CHILD_CATEGORY_OPTIONS } from '../constants/productCategory';

const { Option } = Select;
const { TextArea } = Input;

/** 规格树节点多语言名称展示 */
const SpecTreeTitle: React.FC<{
  nodeData: {
    name?: string;
    nameEn?: string;
    namePl?: string;
    category?: string;
    disabled?: boolean;
  };
}> = ({ nodeData }) => (
  <div
    data-i18n-ignore="true"
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      color: nodeData.disabled ? '#999' : '#333',
      fontSize: 14,
      padding: '2px 0',
    }}
  >
    <span
      style={{
        flexShrink: 0,
        lineHeight: '22px',
        color: nodeData.category === 'option' ? '#1890ff' : '#52c41a',
      }}
    >
      {nodeData.category === 'option' ? '📁' : '📄'}
    </span>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', alignItems: 'center' }}>
      <span>
        <Tag color="red" style={{ marginRight: 4 }}>
          中文
        </Tag>
        {nodeData.name || <span style={{ color: '#bbb' }}>暂无</span>}
      </span>
      <span>
        <Tag color="blue" style={{ marginRight: 4 }}>
          EN
        </Tag>
        {nodeData.nameEn || <span style={{ color: '#bbb' }}>暂无</span>}
      </span>
      <span>
        <Tag color="gold" style={{ marginRight: 4 }}>
          PL
        </Tag>
        {nodeData.namePl || <span style={{ color: '#bbb' }}>暂无</span>}
      </span>
    </div>
  </div>
);

const ProductEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  // 多语言图片状态
  const [i18nLogoUrls, setI18nLogoUrls] = useState<Record<string, string>>({});
  const [i18nDetailCaseNos, setI18nDetailCaseNos] = useState<Record<string, string>>({});
  const [i18nShowCaseNos, setI18nShowCaseNos] = useState<Record<string, string>>({});
  const [i18nDetailFileUrls, setI18nDetailFileUrls] = useState<Record<string, string[]>>({});
  const [i18nShowFileUrls, setI18nShowFileUrls] = useState<Record<string, string[]>>({});
  const [i18nDetailImages, setI18nDetailImages] = useState<Record<string, string[]>>({});
  const [i18nShowImages, setI18nShowImages] = useState<Record<string, string[]>>({});
  const [i18nUploading, setI18nUploading] = useState<Record<string, { logo?: boolean; detail?: boolean; show?: boolean }>>({});
  // 规格相关状态
  const [specTreeData, setSpecTreeData] = useState<any[]>([]);
  const [selectedSpecIds, setSelectedSpecIds] = useState<number[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  const isEdit = !!id;

  useEffect(() => {
    if (isEdit && id) {
      fetchProductDetail(id);
    }
    // 获取规格列表
    fetchSpecList();
  }, [id, isEdit]);


  // 获取规格列表
  const fetchSpecList = async () => {
    try {
      const response = await specService.getSpecTree();
      const specs = response?.data || response || [];
      // 构建树形数据，只显示content类型的规格项
      const buildTreeData = (items: any[]): any[] => {
        return items.map(item => {
          const treeNode: any = {
            title: item.name,
            key: item.id.toString(),
            id: item.id,
            category: item.category,
            name: item.name,
            nameEn: item.nameEn,
            namePl: item.namePl,
            pId: item.pId,
            disabled: item.category !== 'content' // 只有content类型可以选择
          };
          
          if (item.children && item.children.length > 0) {
            treeNode.children = buildTreeData(item.children);
          }
          
          return treeNode;
        });
      };
      
      const treeData = buildTreeData(specs);
      setSpecTreeData(treeData);
    } catch (error) {
      console.error('获取规格列表失败:', error);
      message.error('获取规格列表失败');
    }
  };

  // 获取商品详情
  const fetchProductDetail = async (productId: string) => {
    setLoading(true);
    try {
      const response = await productService.getProductDetail(productId);
      const productData = response.data.shopProduct; // 从AdminShopProductDetailVO中获取shopProduct
      setProduct(productData);
      
      // 设置表单基础数据
      const formValues: any = {
        ...productData,
        // 中文数据使用主表的 name, remark, specialTips（如果没有 i18n 数据）
        name: productData.name,
        remark: productData.remark,
        specialTips: productData.specialTips
      };
      
      // 设置多语言数据
      if (productData.i18n) {
        formValues.i18n = productData.i18n;
        // 设置多语言图片状态
        Object.keys(productData.i18n).forEach(lang => {
          const i18nData = productData.i18n![lang];
          if (i18nData?.logoUrl) {
            setI18nLogoUrls(prev => ({ ...prev, [lang]: i18nData.logoUrl! }));
          }
          if (i18nData?.detailCaseNo) {
            setI18nDetailCaseNos(prev => ({ ...prev, [lang]: i18nData.detailCaseNo! }));
            // 加载详情图片
            commonAttachFileService.listByCaseNo(i18nData.detailCaseNo!).then((response) => {
              if (response?.data && Array.isArray(response.data)) {
                const urls = response.data.map((file) => file.fullUrl);
                setI18nDetailImages(prev => ({ ...prev, [lang]: urls }));
                setI18nDetailFileUrls(prev => ({ ...prev, [lang]: urls }));
              }
            }).catch(() => {});
          }
          if (i18nData?.showCaseNo) {
            setI18nShowCaseNos(prev => ({ ...prev, [lang]: i18nData.showCaseNo! }));
            // 加载展示图片
            commonAttachFileService.listByCaseNo(i18nData.showCaseNo!).then((response) => {
              if (response?.data && Array.isArray(response.data)) {
                const urls = response.data.map((file) => file.fullUrl);
                setI18nShowImages(prev => ({ ...prev, [lang]: urls }));
                setI18nShowFileUrls(prev => ({ ...prev, [lang]: urls }));
              }
            }).catch(() => {});
          }
        });
      } else {
        // 如果没有 i18n 数据，初始化空对象
        formValues.i18n = {
          zh: {
            name: productData.name || '',
            remark: productData.remark || '',
            specialTips: productData.specialTips || '',
            logoUrl: productData.logoUrl || '',
            detailCaseNo: productData.detailCaseNo || '',
            showCaseNo: productData.showCaseNo || '',
            deliveryTime: productData.deliveryTime || ''
          },
          en: { name: '', remark: '', specialTips: '', logoUrl: '', detailCaseNo: '', showCaseNo: '', deliveryTime: '' },
          pl: { name: '', remark: '', specialTips: '', logoUrl: '', detailCaseNo: '', showCaseNo: '', deliveryTime: '' }
        };
      }
      
      form.setFieldsValue(formValues);
      
      // 处理规格
      if (productData.specIds && productData.specIds.length > 0) {
        setSelectedSpecIds(productData.specIds);
        setCheckedKeys(productData.specIds.map(id => id.toString()));
      }
    } catch (error) {
      message.error('获取商品详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 多语言图片上传处理
  const handleI18nImageUpload = async (file: File, lang: string, type: 'logo' | 'detail' | 'show') => {
    setI18nUploading(prev => ({ ...prev, [lang]: { ...prev[lang], [type]: true } }));
    try {
      const uploadUrlResponse = await commonAttachFileService.uploadUrl(file);
      if (uploadUrlResponse.status === 200) {
        const fileUrl = uploadUrlResponse.data;
        if (type === 'logo') {
          setI18nLogoUrls(prev => ({ ...prev, [lang]: fileUrl }));
          form.setFieldsValue({ [`i18n.${lang}.logoUrl`]: fileUrl });
          message.success('LOGO上传成功');
        } else {
          const currentUrls = type === 'detail' ? (i18nDetailFileUrls[lang] || []) : (i18nShowFileUrls[lang] || []);
          const newUrls = [...currentUrls, fileUrl];
          if (type === 'detail') {
            setI18nDetailFileUrls(prev => ({ ...prev, [lang]: newUrls }));
            setI18nDetailImages(prev => ({ ...prev, [lang]: newUrls }));
          } else {
            setI18nShowFileUrls(prev => ({ ...prev, [lang]: newUrls }));
            setI18nShowImages(prev => ({ ...prev, [lang]: newUrls }));
          }
          message.success('图片上传成功');
        }
      }
    } catch (error) {
      message.error('图片上传失败');
    } finally {
      setI18nUploading(prev => ({ ...prev, [lang]: { ...prev[lang], [type]: false } }));
    }
    return false;
  };

  // 删除多语言图片
  const handleRemoveI18nImage = (lang: string, type: 'logo' | 'detail' | 'show', index?: number) => {
    if (type === 'logo') {
      setI18nLogoUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[lang];
        return newUrls;
      });
      form.setFieldsValue({ [`i18n.${lang}.logoUrl`]: '' });
    } else if (type === 'detail' && index !== undefined) {
      const currentUrls = i18nDetailFileUrls[lang] || [];
      const newUrls = currentUrls.filter((_, i) => i !== index);
      setI18nDetailFileUrls(prev => ({ ...prev, [lang]: newUrls }));
      setI18nDetailImages(prev => ({ ...prev, [lang]: newUrls }));
      if (newUrls.length === 0) {
        setI18nDetailCaseNos(prev => {
          const newCaseNos = { ...prev };
          delete newCaseNos[lang];
          return newCaseNos;
        });
        form.setFieldsValue({ [`i18n.${lang}.detailCaseNo`]: '' });
      }
    } else if (type === 'show' && index !== undefined) {
      const currentUrls = i18nShowFileUrls[lang] || [];
      const newUrls = currentUrls.filter((_, i) => i !== index);
      setI18nShowFileUrls(prev => ({ ...prev, [lang]: newUrls }));
      setI18nShowImages(prev => ({ ...prev, [lang]: newUrls }));
      if (newUrls.length === 0) {
        setI18nShowCaseNos(prev => {
          const newCaseNos = { ...prev };
          delete newCaseNos[lang];
          return newCaseNos;
        });
        form.setFieldsValue({ [`i18n.${lang}.showCaseNo`]: '' });
      }
    }
  };

  // 保存商品
  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      // 提取多语言数据
      const i18nData = values.i18n || {};
      
      // 处理多语言图片的caseNo
      const processedI18nData: any = {};
      for (const lang of ['zh', 'en', 'pl']) {
        if (i18nData[lang]) {
          processedI18nData[lang] = { ...i18nData[lang] };
          
          // 处理详情图片caseNo
          if (i18nDetailFileUrls[lang] && i18nDetailFileUrls[lang].length > 0) {
            try {
              const detailBatchResponse = await commonAttachFileService.uploadUrlBatch({
                fileTypeEnum: 'IMAGE',
                urlList: i18nDetailFileUrls[lang]
              });
              if (detailBatchResponse.status === 200) {
                processedI18nData[lang].detailCaseNo = detailBatchResponse.data;
              }
            } catch (error) {
              console.error(`获取${lang}详情图片caseNo失败:`, error);
            }
          }
          
          // 处理展示图片caseNo
          if (i18nShowFileUrls[lang] && i18nShowFileUrls[lang].length > 0) {
            try {
              const showBatchResponse = await commonAttachFileService.uploadUrlBatch({
                fileTypeEnum: 'IMAGE',
                urlList: i18nShowFileUrls[lang]
              });
              if (showBatchResponse.status === 200) {
                processedI18nData[lang].showCaseNo = showBatchResponse.data;
              }
            } catch (error) {
              console.error(`获取${lang}展示图片caseNo失败:`, error);
            }
          }
          
          // 设置logoUrl
          if (i18nLogoUrls[lang]) {
            processedI18nData[lang].logoUrl = i18nLogoUrls[lang];
          }
        }
      }
      
      // 中文数据作为主表数据（如果 i18n.zh 存在则使用，否则使用表单的 name/remark/specialTips）
      const mainName = processedI18nData.zh?.name || values.name || '';
      const mainRemark = processedI18nData.zh?.remark || values.remark || '';
      const mainSpecialTips = processedI18nData.zh?.specialTips || values.specialTips || '';
      
      // 从中文多语言数据中获取主表的图片数据
      const mainLogoUrl = processedI18nData.zh?.logoUrl || '';
      const mainDetailCaseNo = processedI18nData.zh?.detailCaseNo || '';
      const mainShowCaseNo = processedI18nData.zh?.showCaseNo || '';

      const productData = {
        ...values,
        name: mainName, // 主表使用中文数据
        remark: mainRemark,
        specialTips: mainSpecialTips,
        logoUrl: mainLogoUrl,
        detailCaseNo: mainDetailCaseNo,
        showCaseNo: mainShowCaseNo,
        specIds: selectedSpecIds,
        isShow: values.isShow ? 1 : 0,
        isExist: 1,
        i18n: processedI18nData // 传递处理后的多语言数据到后端
      };
      
      if (isEdit) {
        await productService.updateProduct(id!, productData);
        message.success('更新商品成功');
      } else {
        await productService.addProduct(productData);
        message.success('新增商品成功');
      }
      navigate('/products');
    } catch (error) {
      message.error(isEdit ? '更新商品失败' : '新增商品失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理树形选择器的选择
  const handleTreeCheck = (checked: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }, info: any) => {
    // 处理checked参数，可能是数组或对象
    const checkedKeys = Array.isArray(checked) ? checked : checked.checked;
    
    // 只处理选中的节点，过滤掉半选中的父节点
    const checkedNodes = info.checkedNodes || [];
    const selectedIds = checkedNodes
      .filter((node: any) => node.category === 'content')
      .map((node: any) => node.id);
    
    setSelectedSpecIds(selectedIds);
    setCheckedKeys(checkedKeys);
  };

  // 返回列表
  const handleBack = () => {
    navigate('/products');
  };

  return (
    <div>
      <div className="page-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ marginRight: 12 }}
        >
          返回
        </Button>
        <h1 className="page-title">
          {isEdit ? '编辑商品' : '新增商品'}
        </h1>
      </div>

      <Card className="content-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            isShow: 1,
            seq: 0,
            sdkProductId: 10005,
            sdkProductName: '电动车商城'
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="商品名称（中文）"
                name="name"
                rules={[{ required: true, message: '请输入商品名称（中文）' }]}
              >
                <Input placeholder="请输入商品名称（中文）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="商品类型"
                name="category"
                rules={[{ required: true, message: '请选择商品类型' }]}
              >
                <Select placeholder="请选择商品类型">
                  {Object.entries(CATEGORY_MAP).map(([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="多语言配置">
            <Tabs
              items={[
                {
                  key: 'zh',
                  label: '🇨🇳 中文',
                  children: (
                    <div>
                      <Form.Item
                        label="商品名称"
                        name={['i18n', 'zh', 'name']}
                        rules={[{ required: true, message: '请输入商品名称（中文）' }]}
                      >
                        <Input placeholder="请输入商品名称（中文）" />
                      </Form.Item>
                      <Form.Item
                        label="商品描述"
                        name={['i18n', 'zh', 'remark']}
                      >
                        <TextArea rows={4} placeholder="请输入商品描述（中文）" />
                      </Form.Item>
                      <Form.Item
                        label="特殊提示"
                        name={['i18n', 'zh', 'specialTips']}
                      >
                        <Input placeholder="请输入特殊提示（中文）" />
                      </Form.Item>
                      <Form.Item
                        label="物流时效"
                        name={['i18n', 'zh', 'deliveryTime']}
                      >
                        <Input placeholder="请输入物流时效（中文），例如：1 个工作日" />
                      </Form.Item>
                      <Form.Item
                        label="LOGO（中文）"
                        name={['i18n', 'zh', 'logoUrl']}
                      >
                        <div>
                          {i18nLogoUrls['zh'] && (
                            <div style={{ marginBottom: 16, position: 'relative', display: 'inline-block' }}>
                              <Image
                                width={100}
                                height={100}
                                src={i18nLogoUrls['zh']}
                                alt="LOGO"
                                style={{ borderRadius: '6px', objectFit: 'cover' }}
                              />
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                style={{ position: 'absolute', top: -8, right: -8 }}
                                onClick={() => handleRemoveI18nImage('zh', 'logo')}
                              />
                            </div>
                          )}
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'zh', 'logo')}
                            showUploadList={false}
                            disabled={i18nUploading['zh']?.logo}
                          >
                            <Button icon={<UploadOutlined />} loading={i18nUploading['zh']?.logo}>
                              {i18nLogoUrls['zh'] ? '重新上传' : '上传LOGO'}
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                      <Form.Item
                        label="详情图片（中文）"
                        name={['i18n', 'zh', 'detailCaseNo']}
                      >
                        <div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {(i18nDetailImages['zh'] || []).map((url, index) => (
                              <div key={index} style={{ position: 'relative' }}>
                                <Image
                                  width={100}
                                  height={100}
                                  src={url}
                                  alt={`详情图片${index + 1}`}
                                  style={{ borderRadius: '6px', objectFit: 'cover' }}
                                />
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  style={{ position: 'absolute', top: -8, right: -8 }}
                                  onClick={() => handleRemoveI18nImage('zh', 'detail', index)}
                                />
                              </div>
                            ))}
                          </div>
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'zh', 'detail')}
                            showUploadList={false}
                            disabled={i18nUploading['zh']?.detail}
                          >
                            <Button icon={<PlusOutlined />} loading={i18nUploading['zh']?.detail}>
                              添加详情图片
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                      <Form.Item
                        label="展示图片（中文）"
                        name={['i18n', 'zh', 'showCaseNo']}
                      >
                        <div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {(i18nShowImages['zh'] || []).map((url, index) => (
                              <div key={index} style={{ position: 'relative' }}>
                                <Image
                                  width={100}
                                  height={100}
                                  src={url}
                                  alt={`展示图片${index + 1}`}
                                  style={{ borderRadius: '6px', objectFit: 'cover' }}
                                />
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  style={{ position: 'absolute', top: -8, right: -8 }}
                                  onClick={() => handleRemoveI18nImage('zh', 'show', index)}
                                />
                              </div>
                            ))}
                          </div>
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'zh', 'show')}
                            showUploadList={false}
                            disabled={i18nUploading['zh']?.show}
                          >
                            <Button icon={<PlusOutlined />} loading={i18nUploading['zh']?.show}>
                              添加展示图片
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                    </div>
                  )
                },
                {
                  key: 'en',
                  label: '🇺🇸 English',
                  children: (
                    <div>
                      <Form.Item
                        label="商品名称"
                        name={['i18n', 'en', 'name']}
                      >
                        <Input placeholder="请输入商品名称（English）" />
                      </Form.Item>
                      <Form.Item
                        label="商品描述"
                        name={['i18n', 'en', 'remark']}
                      >
                        <TextArea rows={4} placeholder="请输入商品描述（English）" />
                      </Form.Item>
                      <Form.Item
                        label="特殊提示"
                        name={['i18n', 'en', 'specialTips']}
                      >
                        <Input placeholder="请输入特殊提示（English）" />
                      </Form.Item>
                      <Form.Item
                        label="物流时效"
                        name={['i18n', 'en', 'deliveryTime']}
                      >
                        <Input placeholder="请输入物流时效（English），例如：1 business day" />
                      </Form.Item>
                      <Form.Item
                        label="LOGO（English）"
                        name={['i18n', 'en', 'logoUrl']}
                      >
                        <div>
                          {i18nLogoUrls['en'] && (
                            <div style={{ marginBottom: 16, position: 'relative', display: 'inline-block' }}>
                              <Image
                                width={100}
                                height={100}
                                src={i18nLogoUrls['en']}
                                alt="LOGO"
                                style={{ borderRadius: '6px', objectFit: 'cover' }}
                              />
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                style={{ position: 'absolute', top: -8, right: -8 }}
                                onClick={() => handleRemoveI18nImage('en', 'logo')}
                              />
                            </div>
                          )}
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'en', 'logo')}
                            showUploadList={false}
                            disabled={i18nUploading['en']?.logo}
                          >
                            <Button icon={<UploadOutlined />} loading={i18nUploading['en']?.logo}>
                              {i18nLogoUrls['en'] ? '重新上传' : '上传LOGO'}
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                      <Form.Item
                        label="详情图片（English）"
                        name={['i18n', 'en', 'detailCaseNo']}
                      >
                        <div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {(i18nDetailImages['en'] || []).map((url, index) => (
                              <div key={index} style={{ position: 'relative' }}>
                                <Image
                                  width={100}
                                  height={100}
                                  src={url}
                                  alt={`详情图片${index + 1}`}
                                  style={{ borderRadius: '6px', objectFit: 'cover' }}
                                />
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  style={{ position: 'absolute', top: -8, right: -8 }}
                                  onClick={() => handleRemoveI18nImage('en', 'detail', index)}
                                />
                              </div>
                            ))}
                          </div>
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'en', 'detail')}
                            showUploadList={false}
                            disabled={i18nUploading['en']?.detail}
                          >
                            <Button icon={<PlusOutlined />} loading={i18nUploading['en']?.detail}>
                              添加详情图片
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                      <Form.Item
                        label="展示图片（English）"
                        name={['i18n', 'en', 'showCaseNo']}
                      >
                        <div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {(i18nShowImages['en'] || []).map((url, index) => (
                              <div key={index} style={{ position: 'relative' }}>
                                <Image
                                  width={100}
                                  height={100}
                                  src={url}
                                  alt={`展示图片${index + 1}`}
                                  style={{ borderRadius: '6px', objectFit: 'cover' }}
                                />
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  style={{ position: 'absolute', top: -8, right: -8 }}
                                  onClick={() => handleRemoveI18nImage('en', 'show', index)}
                                />
                              </div>
                            ))}
                          </div>
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'en', 'show')}
                            showUploadList={false}
                            disabled={i18nUploading['en']?.show}
                          >
                            <Button icon={<PlusOutlined />} loading={i18nUploading['en']?.show}>
                              添加展示图片
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                    </div>
                  )
                },
                {
                  key: 'pl',
                  label: '🇵🇱 Polski',
                  children: (
                    <div>
                      <Form.Item
                        label="商品名称"
                        name={['i18n', 'pl', 'name']}
                      >
                        <Input placeholder="请输入商品名称（Polski）" />
                      </Form.Item>
                      <Form.Item
                        label="商品描述"
                        name={['i18n', 'pl', 'remark']}
                      >
                        <TextArea rows={4} placeholder="请输入商品描述（Polski）" />
                      </Form.Item>
                      <Form.Item
                        label="特殊提示"
                        name={['i18n', 'pl', 'specialTips']}
                      >
                        <Input placeholder="请输入特殊提示（Polski）" />
                      </Form.Item>
                      <Form.Item
                        label="物流时效"
                        name={['i18n', 'pl', 'deliveryTime']}
                      >
                        <Input placeholder="请输入物流时效（Polski），例如：1 dzień roboczy" />
                      </Form.Item>
                      <Form.Item
                        label="LOGO（Polski）"
                        name={['i18n', 'pl', 'logoUrl']}
                      >
                        <div>
                          {i18nLogoUrls['pl'] && (
                            <div style={{ marginBottom: 16, position: 'relative', display: 'inline-block' }}>
                              <Image
                                width={100}
                                height={100}
                                src={i18nLogoUrls['pl']}
                                alt="LOGO"
                                style={{ borderRadius: '6px', objectFit: 'cover' }}
                              />
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                style={{ position: 'absolute', top: -8, right: -8 }}
                                onClick={() => handleRemoveI18nImage('pl', 'logo')}
                              />
                            </div>
                          )}
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'pl', 'logo')}
                            showUploadList={false}
                            disabled={i18nUploading['pl']?.logo}
                          >
                            <Button icon={<UploadOutlined />} loading={i18nUploading['pl']?.logo}>
                              {i18nLogoUrls['pl'] ? '重新上传' : '上传LOGO'}
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                      <Form.Item
                        label="详情图片（Polski）"
                        name={['i18n', 'pl', 'detailCaseNo']}
                      >
                        <div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {(i18nDetailImages['pl'] || []).map((url, index) => (
                              <div key={index} style={{ position: 'relative' }}>
                                <Image
                                  width={100}
                                  height={100}
                                  src={url}
                                  alt={`详情图片${index + 1}`}
                                  style={{ borderRadius: '6px', objectFit: 'cover' }}
                                />
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  style={{ position: 'absolute', top: -8, right: -8 }}
                                  onClick={() => handleRemoveI18nImage('pl', 'detail', index)}
                                />
                              </div>
                            ))}
                          </div>
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'pl', 'detail')}
                            showUploadList={false}
                            disabled={i18nUploading['pl']?.detail}
                          >
                            <Button icon={<PlusOutlined />} loading={i18nUploading['pl']?.detail}>
                              添加详情图片
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                      <Form.Item
                        label="展示图片（Polski）"
                        name={['i18n', 'pl', 'showCaseNo']}
                      >
                        <div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {(i18nShowImages['pl'] || []).map((url, index) => (
                              <div key={index} style={{ position: 'relative' }}>
                                <Image
                                  width={100}
                                  height={100}
                                  src={url}
                                  alt={`展示图片${index + 1}`}
                                  style={{ borderRadius: '6px', objectFit: 'cover' }}
                                />
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  style={{ position: 'absolute', top: -8, right: -8 }}
                                  onClick={() => handleRemoveI18nImage('pl', 'show', index)}
                                />
                              </div>
                            ))}
                          </div>
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'pl', 'show')}
                            showUploadList={false}
                            disabled={i18nUploading['pl']?.show}
                          >
                            <Button icon={<PlusOutlined />} loading={i18nUploading['pl']?.show}>
                              添加展示图片
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                    </div>
                  )
                },
              ]}
            />
          </Form.Item>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="商品子类"
                name="childCategory"
              >
                <Select placeholder="请选择商品子类">
                  {CHILD_CATEGORY_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="排序"
                name="seq"
                rules={[{ required: true, message: '请输入排序' }]}
              >
                <InputNumber 
                  placeholder="请输入排序" 
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="原价"
                name="price"
                rules={[{ required: true, message: '请输入原价' }]}
              >
                <InputNumber 
                  placeholder="请输入原价" 
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="折扣价"
                name="discountPrice"
              >
                <InputNumber 
                  placeholder="请输入折扣价" 
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="物流费用"
                name="deliveryFee"
                tooltip="例如：400，前台统一展示为 zł"
              >
                <InputNumber
                  placeholder="请输入物流费用"
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>


          <Form.Item
            label="商品规格"
          >
            <div
              data-i18n-ignore="true"
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '8px',
                maxHeight: '360px',
                overflow: 'auto',
                backgroundColor: '#fafafa',
              }}
            >
              <Tree
                checkable
                checkedKeys={checkedKeys}
                onCheck={handleTreeCheck}
                treeData={specTreeData}
                showLine
                showIcon={false}
                style={{ background: 'transparent' }}
                titleRender={(nodeData: any) => <SpecTreeTitle nodeData={nodeData} />}
              />
            </div>
            <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
              已选择 {selectedSpecIds.length} 个规格（只能选择具体规格项）
            </div>
          </Form.Item>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="是否上架"
                name="isShow"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="上架" 
                  unCheckedChildren="下架" 
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              style={{ marginRight: 12 }}
            >
              {isEdit ? '更新' : '保存'}
            </Button>
            <Button onClick={handleBack}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ProductEdit;
